import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/brand";
import BrandMark from "../components/BrandMark";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

const passwordChecks = (pw) => ({
  length: pw.length >= 8,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  number: /\d/.test(pw),
  symbol: /[^A-Za-z0-9]/.test(pw),
});

function PasswordRule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 ${ok ? "text-[#279153]" : "text-[#5C7064]"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </li>
  );
}

function formatError(detail) {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState("email"); // email | reset
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const checks = passwordChecks(newPassword);
  const passwordValid = Object.values(checks).every(Boolean);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success("If an account exists, a code is on its way");
      setStep("reset");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error("Enter the 6-digit code"); return; }
    if (!passwordValid) { toast.error("Pick a stronger password"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code, new_password: newPassword });
      toast.success("Password updated — please sign in");
      nav("/login");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    try {
      await axios.post(`${API}/auth/resend-code`, { email, purpose: "reset" });
      toast.success("Code re-sent");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="forgot-page" className="pt-32 pb-24 min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md p-10 bg-[#09120C] border border-[#1A2B20]">
        <BrandMark variant="knot" className="h-12 w-12 mb-6 block" />

        {step === "email" ? (
          <form onSubmit={requestCode}>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Password reset</div>
            <h1 className="font-serif-display text-3xl text-white mb-3">Forgot your password?</h1>
            <p className="text-sm text-[#95A89C] mb-6">Enter your email and we'll send you a code to set a new one.</p>

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Email</label>
            <input
              data-testid="forgot-email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-6"
            />

            <button
              type="submit"
              data-testid="forgot-submit"
              disabled={loading}
              className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              {loading ? "…" : "Send code"}
            </button>

            <p className="mt-6 text-sm text-[#95A89C] text-center">
              Remembered it?{" "}
              <Link to="/login" className="text-[#279153] hover:underline">Back to sign in</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submitReset}>
            <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-2">Reset password</div>
            <h1 className="font-serif-display text-3xl text-white mb-3">Set a new password</h1>
            <p className="text-sm text-[#95A89C] mb-6">We sent a code to <span className="text-white">{email}</span>.</p>

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">Verification code</label>
            <input
              data-testid="reset-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              maxLength={6}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-lg tracking-[0.4em] text-center focus:border-[#279153] focus:outline-none mb-5 font-mono"
            />

            <label className="block text-[10px] tracking-[0.3em] uppercase text-[#5C7064] mb-2">New password</label>
            <input
              data-testid="reset-new-password"
              type="password"
              required
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="w-full px-4 py-3 bg-[#050605] border border-[#1A2B20] text-white text-sm focus:border-[#279153] focus:outline-none mb-4"
            />

            <ul className="text-xs space-y-1.5 mb-6">
              <PasswordRule ok={checks.length} label="At least 8 characters" />
              <PasswordRule ok={checks.upper} label="One uppercase letter" />
              <PasswordRule ok={checks.lower} label="One lowercase letter" />
              <PasswordRule ok={checks.number} label="One number" />
              <PasswordRule ok={checks.symbol} label="One symbol" />
            </ul>

            <button
              type="submit"
              data-testid="reset-submit"
              disabled={loading || !passwordValid || code.length !== 6}
              className="w-full px-6 py-4 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase disabled:opacity-50"
            >
              {loading ? "…" : "Reset password"}
            </button>

            <div className="mt-6 flex justify-between text-xs">
              <button type="button" onClick={() => setStep("email")} className="text-[#95A89C] hover:text-white tracking-[0.2em] uppercase">
                ← Back
              </button>
              <button type="button" onClick={resendCode} className="text-[#279153] hover:underline tracking-[0.2em] uppercase">
                Resend code
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
